import { useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import './Activity.css';
import { useRevit } from '../../contexts/RevitContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faHandPointer, faEnvelope, faPhone, faDollarSign, faChevronDown, faChevronUp, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import ManualSale from '../ManualSale/ManualSale';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import Icon from '../Icon';
import { ResourceSourceLogo, ReferrerSourceLogo } from '../SourceLogo/SourceLogo';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const countryFlag = (code) => {
    if (!code || code.length !== 2) return '';
    return String.fromCodePoint(...code.toUpperCase().split('').map((char) => 0x1F1E6 + char.charCodeAt(0) - 65));
};

const countryName = (code) => {
    if (!code || code.length !== 2) return '';
    const upper = code.toUpperCase();
    try {
        return regionNames.of(upper) || upper;
    } catch {
        return upper;
    }
};

const getResourceDisplayName = (resource) => {
    if (resource.resource_type === 'qr' && resource.youtube_title) {
        return resource.youtube_title;
    }

    return resource.youtube_title || resource.resource_name;
};

const getReferrerHost = (referrer) => {
    if (!referrer || !String(referrer).trim()) return 'direct';
    const value = String(referrer).trim();
    try {
        const url = new URL(value.includes('://') ? value : `https://${value}`);
        return url.hostname.replace(/^www\./i, '') || 'direct';
    } catch {
        return value.replace(/^www\./i, '').split(/[/?#]/)[0] || 'direct';
    }
};

const hasResource = (conversion) => !!(conversion.resource_name || conversion.youtube_title || conversion.resource_type);

const hasReferrer = (conversion) => !!(conversion.initial_referrer && String(conversion.initial_referrer).trim());

const isDirectSource = (item) => !hasResource(item) && !hasReferrer(item);

const canShowInActivity = (item) => {
    if (hasResource(item) || hasReferrer(item)) return true;
    const type = item.conversionType || item.conversion_type || item.event_type;
    return !!type && type !== 'click';
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getLeadEmails = (conversion) => (
    Array.isArray(conversion?.emails) ? conversion.emails : []
)
    .map((email) => String(email || '').trim())
    .filter(Boolean);

const getLeadName = (conversion) => {
    const name = conversion?.name;
    return name && String(name).trim() ? String(name).trim() : '';
};

const getConversionDbId = (conversion) => {
    if (!conversion) return null;
    if (conversion.conversionType === 'call_booking' || conversion.conversion_type === 'call_booking') return conversion.booking_id;
    if (conversion.conversionType === 'funnel_step' || conversion.conversion_type === 'funnel_step') return conversion.conversion_id;
    if (conversion.conversionType === 'email_signup' || conversion.conversion_type === 'email_signup') return conversion.signup_id;
    return conversion.sale_id || null;
};

const getConversionRowId = (conversion, index) => String(
    getConversionDbId(conversion)
    || `${conversion.lead_id || 'conversion'}-${index}`
);

const getEventActionLabel = (event) => {
    if (event.event_type === 'click') return 'Clicked';
    if (event.event_type === 'sale') return 'Conversion';
    if (event.event_type === 'call_booking') return 'Call booking';
    if (event.event_type === 'email_signup') return 'Email signup';
    if (event.event_type === 'funnel_step') return event.step_name || 'Funnel step';
    return 'Event';
};

const Activity = ({ isProAccount, openBillingPortal }) => {
    const { username } = useRevit();
    const [selectedOffer, setSelectedOffer] = useState('all');
    const {getToken} = useAuth();
    const [expandedItems, setExpandedItems] = useState({});
    const [openPathId, setOpenPathId] = useState(null);
    const queryClient = useQueryClient();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [conversionToDelete, setConversionToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showManualSaleModal, setShowManualSaleModal] = useState(false);
    const [showRecordSaleModal, setShowRecordSaleModal] = useState(false);
    const [callBookingToConvert, setCallBookingToConvert] = useState(null);
    const [isRecordingSale, setIsRecordingSale] = useState(false);
    const [saleDate, setSaleDate] = useState('');
    const [salePrice, setSalePrice] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;
    const [showAllFunnels, setShowAllFunnels] = useState(false);
    const [showPaths, setShowPaths] = useState(false);
    const [hideUnattributed, setHideUnattributed] = useState(true);
    const [editingConversionValue, setEditingConversionValue] = useState(null);
    const [newConversionValue, setNewConversionValue] = useState('');
    const [isUpdatingValue, setIsUpdatingValue] = useState(false);
    const [addingEmailKey, setAddingEmailKey] = useState(null);
    const [emailDraft, setEmailDraft] = useState('');
    const [nameDraft, setNameDraft] = useState('');
    const [emailError, setEmailError] = useState('');
    const [isSavingEmail, setIsSavingEmail] = useState(false);
    const { setIsProModalOpen } = useRevit();

    // Function to handle delete conversion
    const handleDeleteConversion = (conversionId, conversionType) => {
        setConversionToDelete({ id: conversionId, type: conversionType });
        setShowDeleteConfirmation(true);
    };

    // Function to open manual sale modal
    const openManualSaleModal = () => {
        setShowManualSaleModal(true);
    };

    // Function to close manual sale modal
    const closeManualSaleModal = () => {
        setShowManualSaleModal(false);
        // Refresh conversions data when modal is closed
        queryClient.invalidateQueries(['latestConversions', username]);
    };

    // Function to confirm and execute deletion
    const confirmDeleteConversion = async () => {
        if (!conversionToDelete) return;
        
        setIsDeleting(true);
        
        // Optimistically update the UI by removing the conversion immediately
        const previousData = queryClient.getQueryData(['latestConversions', username]);
        
        queryClient.setQueryData(['latestConversions', username], (old) => {
            if (!old) return old;
            
            const conversionKey = conversionToDelete.type === 'sale' ? 'sales' : 
                                 conversionToDelete.type === 'funnel_step' ? 'other_conversions' :
                                 conversionToDelete.type === 'call_booking' ? 'other_conversions' :
                                 conversionToDelete.type === 'email_signup' ? 'other_conversions' : null;
            
            if (!conversionKey) return old;
            
            return {
                ...old,
                [conversionKey]: old[conversionKey]?.filter(conv => {
                    const convId = conversionToDelete.type === 'call_booking' ? conv.booking_id :
                                  conversionToDelete.type === 'funnel_step' ? conv.conversion_id :
                                  conversionToDelete.type === 'email_signup' ? conv.signup_id :
                                  conv.sale_id;
                    return convId !== conversionToDelete.id;
                }) || []
            };
        });
        
        try {
            const token = await getToken();
            const response = await axios.delete(
                `https://${SERVER_URL}/api/delete-conversion/${username}/${conversionToDelete.id}/${conversionToDelete.type}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            
            if (response.status === 200) {
                setShowDeleteConfirmation(false);
                setConversionToDelete(null);
            } else {
                // Rollback on failure
                queryClient.setQueryData(['latestConversions', username], previousData);
                alert('Failed to delete conversion');
            }
        } catch (error) {
            console.error('Error deleting conversion:', error);
            // Rollback on error
            queryClient.setQueryData(['latestConversions', username], previousData);
            alert('Error deleting conversion');
        } finally {
            setIsDeleting(false);
        }
    };

    // Function to cancel deletion
    const cancelDeleteConversion = () => {
        setShowDeleteConfirmation(false);
        setConversionToDelete(null);
    };

    const toggleExpand = (index, pathId) => {
        setExpandedItems(prev => {
            const currentlyOpen = Boolean(prev[index]) || openPathId === pathId;
            return {
                ...prev,
                [index]: !currentlyOpen
            };
        });
        setOpenPathId(current => (current === pathId ? null : current));
    };

    // Function to handle record sale for call booking
    const handleRecordSale = (callBooking) => {
        setCallBookingToConvert(callBooking);
        setShowRecordSaleModal(true);
        // Set default date to today
        setSaleDate(new Date().toISOString().split('T')[0]);
        setSalePrice('');
    };

    // Function to confirm and execute sale recording
    const confirmRecordSale = async () => {
        if (!callBookingToConvert || !saleDate) return;
        
        setIsRecordingSale(true);
        try {
            const token = await getToken();
            
            const resourceClicked = hasResource(callBookingToConvert)
                ? {
                    type: callBookingToConvert.resource_type,
                    name: callBookingToConvert.resource_name
                }
                : null;
            
            const payload = {
                offer: callBookingToConvert.offer_name,
                lead_id: callBookingToConvert.lead_id || null,
                date: saleDate,
                initial_referrer: callBookingToConvert.initial_referrer || null
            };
            if (resourceClicked) {
                payload.resourceClicked = resourceClicked;
            }
            
            // Add price if provided
            if (salePrice && salePrice.trim() !== '') {
                payload.price = parseFloat(salePrice);
            }
            
            const response = await axios.post(
                `https://${SERVER_URL}/api/purchase/${username}`,
                payload,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            
            if (response.status === 200) {
                // Invalidate and refetch the conversions data
                queryClient.invalidateQueries(['latestConversions', username]);
                setShowRecordSaleModal(false);
                setCallBookingToConvert(null);
                setSaleDate('');
                setSalePrice('');
                alert('Sale recorded successfully!');
            } else {
                alert('Failed to record sale');
            }
        } catch (error) {
            console.error('Error recording sale:', error);
            alert('Error recording sale');
        } finally {
            setIsRecordingSale(false);
        }
    };

    // Function to cancel sale recording
    const cancelRecordSale = () => {
        setShowRecordSaleModal(false);
        setCallBookingToConvert(null);
        setSaleDate('');
        setSalePrice('');
    };

    // Function to handle editing conversion value
    const handleEditConversionValue = (conversion) => {
        setEditingConversionValue(conversion.sale_id || conversion.conversion_id);
        setNewConversionValue(conversion.value || '');
    };

    // Function to save edited conversion value
    const handleSaveConversionValue = async () => {
        if (!editingConversionValue || !newConversionValue) return;
        
        setIsUpdatingValue(true);
        try {
            const token = await getToken();
            const response = await axios.post(
                `https://${SERVER_URL}/api/update-conversion-value/${username}`,
                {
                    conversionId: editingConversionValue,
                    newValue: parseFloat(newConversionValue)
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            
            if (response.status === 200) {
                // Invalidate and refetch the conversions data
                queryClient.invalidateQueries(['latestConversions', username]);
                setEditingConversionValue(null);
                setNewConversionValue('');
            } else {
                alert('Failed to update conversion value');
            }
        } catch (error) {
            console.error('Error updating conversion value:', error);
            alert('Error updating conversion value');
        } finally {
            setIsUpdatingValue(false);
        }
    };

    // Function to cancel editing conversion value
    const handleCancelEditConversionValue = () => {
        setEditingConversionValue(null);
        setNewConversionValue('');
    };

    const openAddEmail = (rowId) => {
        setAddingEmailKey(rowId);
        setEmailDraft('');
        setNameDraft('');
        setEmailError('');
    };

    const cancelAddEmail = () => {
        setAddingEmailKey(null);
        setEmailDraft('');
        setNameDraft('');
        setEmailError('');
    };

    const saveLeadContact = async (conversion) => {
        const email = emailDraft.trim().toLowerCase();
        const name = nameDraft.trim().slice(0, 200);
        if (!email && !name) {
            setEmailError('Enter an email or a name.');
            return;
        }
        if (email && !EMAIL_PATTERN.test(email)) {
            setEmailError('Enter a valid email.');
            return;
        }

        const previousLeadId = conversion.lead_id ? String(conversion.lead_id) : '';
        const conversionId = getConversionDbId(conversion);
        if (conversionId == null) {
            setEmailError('Could not save.');
            return;
        }

        setIsSavingEmail(true);
        setEmailError('');
        try {
            const token = await getToken();
            const headers = { Authorization: `Bearer ${token}` };
            let savedLeadId = previousLeadId;
            let savedEmail = '';
            let savedName = '';
            let matchedExistingLead = false;

            if (email) {
                const response = await axios.post(
                    `https://${SERVER_URL}/api/add-email-for-conversion/${username}`,
                    {
                        email,
                        conversionType: conversion.conversionType,
                        conversionId,
                    },
                    { headers }
                );
                savedLeadId = response.data?.leadId || savedLeadId;
                savedEmail = email;
                matchedExistingLead = Boolean(response.data?.alreadyExisted || response.data?.merged || response.data?.reassigned);
            }

            if (name) {
                const response = await axios.post(
                    `https://${SERVER_URL}/api/add-name-for-conversion/${username}`,
                    {
                        name,
                        conversionType: conversion.conversionType,
                        conversionId,
                    },
                    { headers }
                );
                savedLeadId = response.data?.leadId || savedLeadId;
                savedName = response.data?.name || name;
            }

            const withContact = (items) => (items || []).map((item) => {
                const itemType = item.conversionType || item.conversion_type;
                const sameConversion = conversionId != null
                    && itemType === conversion.conversionType
                    && String(getConversionDbId(item)) === String(conversionId);
                const sameLead = previousLeadId && String(item.lead_id) === previousLeadId;
                if (!sameConversion && !sameLead) return item;
                const next = { ...item };
                if (savedLeadId) next.lead_id = savedLeadId;
                if (savedEmail) {
                    const emails = getLeadEmails(item);
                    next.emails = emails.some((value) => value.toLowerCase() === savedEmail)
                        ? emails
                        : [...emails, savedEmail];
                }
                if (savedName) next.name = savedName;
                return next;
            });

            if (!matchedExistingLead) {
                queryClient.setQueryData(['latestConversions', username], (old) => {
                    if (!old) return old;
                    const next = { ...old };
                    if (old.sales) next.sales = withContact(old.sales);
                    if (old.stripe_sales) next.stripe_sales = withContact(old.stripe_sales);
                    if (old.other_conversions) next.other_conversions = withContact(old.other_conversions);
                    return next;
                });
            }
            await queryClient.refetchQueries({ queryKey: ['latestConversions', username] });
            if (matchedExistingLead) setOpenPathId(`${conversion.conversionType}:${conversionId}`);
            queryClient.invalidateQueries(['audienceLeads', username]);
            if (savedLeadId) queryClient.invalidateQueries(['audienceJourney', username, savedLeadId]);
            if (previousLeadId && previousLeadId !== savedLeadId) {
                queryClient.invalidateQueries(['audienceJourney', username, previousLeadId]);
            }
            cancelAddEmail();
        } catch (error) {
            setEmailError(error.response?.data?.error || 'Could not save.');
        } finally {
            setIsSavingEmail(false);
        }
    };

    // Fetch latest conversions
    const { data, error: conversionsError, isLoading: isLoadingConversions } = useQuery({
        queryKey: ['latestConversions', username],
        queryFn: async () => {
            const token = await getToken();
            const response = await axios.get(`https://${SERVER_URL}/api/get-latest-conversions/${username}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            console.log('Activity conversions:', response.data);
            return response.data;
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 10 * 60 * 1000, // 10 minutes
    });

    // Combine all conversions and sort by timestamp
    const allConversions = useMemo(() => {
        if (!data) return [];
        
        const stripeSales = (data.stripe_sales || []).map(sale => ({
            ...sale,
            isStripeSale: true,
            conversionType: 'sale'
        }));
        
        // Create a set of unique identifiers for stripe sales to filter regular sales
        const stripeSaleIdentifiers = new Set(
            stripeSales
                .filter(sale => sale.lead_id && sale.offer_name)
                .map(sale => `${sale.lead_id}-${sale.offer_name}`)
        );
        
        // Filter out regular sales that have the same lead_id and offer_name as any stripe sale
        const regularSales = (data.sales || [])
            .filter(sale => {
                // Keep sales that don't have a lead_id or don't match any stripe sale
                return !sale.offer_name || 
                       !stripeSaleIdentifiers.has(`${sale.lead_id}-${sale.offer_name}`);
            })
            .map(sale => ({
                ...sale,
                isStripeSale: false,
                conversionType: 'sale'
            }));
        
        // Map other conversions to include conversionType
        const otherConvs = (data.other_conversions || []).map(conv => ({
            ...conv,
            conversionType: conv.conversion_type,
            related_events: conv.related_events || []
        }));
        
        // Combine all conversions and sort by timestamp (newest first)
        const combined = [...regularSales, ...stripeSales, ...otherConvs]
            .filter(canShowInActivity)
            .map((conversion) => ({
                ...conversion,
                related_events: (conversion.related_events || []).filter(canShowInActivity)
            }));

        return combined.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [data]);

    // Filter conversions by selected offer
    const filteredConversions = allConversions
        .filter(conversion => selectedOffer === 'all' || conversion.offer_name === selectedOffer);

    const listedConversions = hideUnattributed
        ? filteredConversions.filter((conversion) => !isDirectSource(conversion))
        : filteredConversions;
    
    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = listedConversions.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(listedConversions.length / itemsPerPage);
    
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };
    
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };
    
    // Get unique offer names for the dropdown
    const offerNames = [...new Set(allConversions.map(conversion => conversion.offer_name).filter(Boolean))];

    // Calculate average clicks to conversion and time to conversion
    const { averageClicksToConversion, averageTimeToConversion } = useMemo(() => {
        if (!allConversions || allConversions.length === 0) return { 
            averageClicksToConversion: null, 
            averageTimeToConversion: null
        };
        
        // Filter conversions by selected offer if not "all"
        const filteredByOffer = selectedOffer === 'all' 
            ? allConversions 
            : allConversions.filter(conversion => conversion.offer_name === selectedOffer);
        
        const conversionsWithClicks = filteredByOffer
            .filter(conversion => conversion.related_events && conversion.related_events.some(event => event.event_type === 'click'))
            .slice(0, 20); // Limit to 20 most recent conversions
        
        if (conversionsWithClicks.length === 0) return { 
            averageClicksToConversion: null, 
            averageTimeToConversion: null
        };
        
        const totalClicks = conversionsWithClicks.reduce((sum, conversion) => 
            sum + conversion.related_events.filter(event => event.event_type === 'click').length, 0);
        
        // Calculate time to conversion (from first click to conversion)
        let totalTimeToConversion = 0;
        let conversionsWithTime = 0;
        
        conversionsWithClicks.forEach(conversion => {
            if (conversion.related_events && conversion.related_events.length > 0) {
                // Get only click events and sort by timestamp (oldest first)
                const clickEvents = conversion.related_events
                    .filter(event => event.event_type === 'click')
                    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                
                if (clickEvents.length > 0) {
                    const firstClickTime = new Date(clickEvents[0].timestamp);
                    const conversionTime = new Date(conversion.timestamp);
                    
                    // Calculate time difference in milliseconds
                    const timeDiff = conversionTime - firstClickTime;
                    
                    if (timeDiff >= 0) { // Ensure valid time difference
                        totalTimeToConversion += timeDiff;
                        conversionsWithTime++;
                    }
                }
            }
        });
        
        // Calculate average time to conversion in milliseconds
        const avgTimeMs = conversionsWithTime > 0 ? totalTimeToConversion / conversionsWithTime : null;
        
        // Format time to days and hours
        let formattedTime = null;
        if (avgTimeMs) {
            const days = Math.floor(avgTimeMs / (1000 * 60 * 60 * 24));
            const hours = Math.floor((avgTimeMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            formattedTime = { days, hours };
        }
        
        return {
            averageClicksToConversion: (totalClicks / conversionsWithClicks.length).toFixed(1),
            conversionsCount: conversionsWithClicks.length,
            averageTimeToConversion: formattedTime
        };
    }, [allConversions, selectedOffer]);

    // Add this function to analyze common funnels
    const analyzeCommonFunnels = (conversions) => {
        // Group conversions by lead_id to avoid counting the same sequence multiple times
        const conversionsByLead = {};
        conversions.forEach(conversion => {
            if (conversion.lead_id) {
                if (!conversionsByLead[conversion.lead_id]) {
                    conversionsByLead[conversion.lead_id] = [];
                }
                conversionsByLead[conversion.lead_id].push(conversion);
            }
        });
        
        const sequences = [];
        
        // For each lead, use the conversion with the most complete related_events
        Object.values(conversionsByLead).forEach(leadConversions => {
            // Find the conversion with the most related events (most complete sequence)
            const mostCompleteConversion = leadConversions.reduce((max, current) => {
                const maxEvents = max.related_events?.length || 0;
                const currentEvents = current.related_events?.length || 0;
                return currentEvents > maxEvents ? current : max;
            });
            
            if (mostCompleteConversion.related_events && mostCompleteConversion.related_events.length > 1) {
                const sortedEvents = mostCompleteConversion.related_events
                    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
                
                // Create sequence of resource interactions
                const sequence = sortedEvents.map(event => ({
                    resource: getResourceDisplayName(event),
                    type: event.resource_type,
                    event_type: event.event_type,
                    offer_type: event.offer_type
                }));
                
                sequences.push(sequence);
            }
        });
        
        // Find common patterns (sequences of 2+ steps)
        const patterns = {};
        
        sequences.forEach(sequence => {
            // Check all possible subsequences of length 2+
            for (let i = 0; i < sequence.length - 1; i++) {
                for (let j = i + 2; j <= sequence.length; j++) {
                    const subSequence = sequence.slice(i, j);
                    
                    // Get unique resources in this subsequence
                    const uniqueResources = new Set(subSequence.map(step => step.resource));
                    
                    // Skip patterns that only involve one resource repeated
                    if (uniqueResources.size <= 1) {
                        continue;
                    }
                    
                    const patternKey = subSequence
                        .map(step => {
                            if (step.event_type === 'click') {
                                return `${step.resource}(${step.event_type})`;
                            } else if (step.event_type === 'sale') {
                                return step.offer_type === 'signup' ? 'email_signup' : 'sale_paid';
                            } else {
                                return step.event_type;
                            }
                        })
                        .join(' → ');
                    
                    patterns[patternKey] = (patterns[patternKey] || 0) + 1;
                }
            }
        });
        
        // Return patterns that occur more than once, sorted by frequency
        const allPatterns = Object.entries(patterns)
            .filter(([pattern, count]) => count > 1)
            .sort((a, b) => b[1] - a[1])
            .map(([pattern, count]) => {
                // Check if the final event in the pattern is a click
                const finalEvent = pattern.split(' → ').pop();
                const isClick = finalEvent.includes('(click)');
                return [pattern, count, isClick ? 'clicks' : 'conversions'];
            });
           
        return allPatterns;
    };

    // Add this to your component state or useMemo
    const commonFunnels = useMemo(() => {
        if (filteredConversions.length > 0) {
            return analyzeCommonFunnels(filteredConversions);
        }
        return [];
    }, [filteredConversions]);

    if (isLoadingConversions) {
        return (
            <div className="activity-container">
                <div className="loading-overlay">
                    <div className="loading-text">Loading...</div>
                </div>
            </div>
        );
    }

    if (conversionsError) {
        return (
            <div className="activity-container">
                <div className="error-message">
                    Error loading conversions data
                </div>
            </div>
        );
    }

    return (
        <div className="activity-container">
            <div className="activity-header-row">
                <p className="body-large-semibold">Conversion activity</p>
            </div>

            <div className="above-convs-container">
                <div className='conversion-insight-container body-medium-regular'>
                    {averageClicksToConversion && (
                        <div className="conversion-insight conversion-insight-small">
                            <Icon name="click" className="" color="var(--Primary-500)" />
                            <p>
                                On average, it took <strong>{averageClicksToConversion}</strong> clicks for your leads to convert
                            </p>
                        </div>
                    )}
                    
                    {isProAccount && averageTimeToConversion ? (
                        <div className="conversion-insight conversion-insight-small">
                            <Icon name="lightning" className="" />
                            <p>
                                On average, it took <strong>
                                    {averageTimeToConversion.days > 0 ? `${averageTimeToConversion.days}d` : ''}
                                    {averageTimeToConversion.days > 0 && averageTimeToConversion.hours > 0 ? ' ' : ''}
                                    {averageTimeToConversion.hours > 0 ? `${averageTimeToConversion.hours}h` : ''}
                                    {!averageTimeToConversion.days && !averageTimeToConversion.hours ? '< 1h' : ''}
                                </strong> from first click to conversion
                            </p>
                        </div>
                    ) : !isProAccount ? (
                        <div className="conversion-insight conversion-insight-small pro-upgrade-insight">
                            <Icon name="lightning" className="" />
                            <p>
                                <span className="pro-badge">PRO</span> <button onClick={() => setIsProModalOpen(true)} className="upgrade-prompt">Upgrade to Pro</button> for more insights
                            </p>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Common Funnels Section */}
            {commonFunnels.length > 0 && (
                <div className="common-funnels-section">
                    <button 
                        className="paths-toggle-btn"
                        onClick={() => setShowPaths(!showPaths)}
                    >
                        <span className="paths-toggle-text">Common Customer Paths</span>
                        <FontAwesomeIcon icon={showPaths ? faChevronUp : faChevronDown} />
                    </button>
                    
                    {showPaths && (
                        <div className="funnels-list-container">
                            <div className="funnels-list">
                                {(showAllFunnels ? commonFunnels : commonFunnels.slice(0, 5)).map(([pattern, count, eventType], index) => (
                                    <div key={index} className="funnel-pattern">
                                        <div className="pattern-sequence">
                                            {pattern.split(' → ').map((step, stepIndex) => (
                                                <span key={stepIndex}>
                                                    {stepIndex > 0 && ' → '}
                                                    {step === 'email_signup' ? 'Email Signup' : 
                                                     step === 'sale_paid' ? 'Sale' : 
                                                     step === 'call_booking' ? 'Call Booking' : 
                                                     step}
                                                    {step === 'email_signup' && <FontAwesomeIcon icon={faEnvelope} className="path-icon email-path-icon" />}
                                                    {step === 'sale_paid' && <FontAwesomeIcon icon={faDollarSign} className="path-icon sale-path-icon" />}
                                                    {step === 'call_booking' && <FontAwesomeIcon icon={faPhone} className="path-icon call-path-icon" />}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="pattern-count">{count} {eventType}</div>
                                    </div>
                                ))}
                            </div>
                            {commonFunnels.length > 5 && (
                                <button 
                                    className="show-more-funnels-button"
                                    onClick={(e) => { e.stopPropagation(); setShowAllFunnels(!showAllFunnels); }}
                                >
                                    {showAllFunnels ? 'Show Less' : 'Show More'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="filter-container-activity">
                <div className="activity-filter-controls">
                    <div className="control-group-activity">
                        <label htmlFor="offer-filter-activity body-small-regular">Filter by:</label>
                        <select
                            id="offer-filter-activity"
                            value={selectedOffer}
                            onChange={(e) => setSelectedOffer(e.target.value)}
                        >
                            <option value="all">All Offers</option>
                            {offerNames.map((offerName, index) => (
                                <option key={index} value={offerName}>
                                    {offerName}
                                </option>
                            ))}
                        </select>
                    </div>
                    <label className="hide-unattributed-toggle">
                        <span>Hide unattributed conversions</span>
                        <span className="hide-unattributed-switch">
                            <input
                                type="checkbox"
                                className="hide-unattributed-switch-input"
                                checked={hideUnattributed}
                                onChange={(e) => {
                                    setHideUnattributed(e.target.checked);
                                    setCurrentPage(1);
                                }}
                            />
                            <span className="hide-unattributed-switch-track">
                                <span className="hide-unattributed-switch-thumb" />
                            </span>
                        </span>
                    </label>
                </div>
                <button 
                    className="manual-conversion-button"
                    onClick={openManualSaleModal}
                >
                    <FontAwesomeIcon icon={faHandPointer} /> Record Manual Conversion
                </button>
            </div>

            {currentItems.length > 0 ? (
                <>
                    <ul className="conversion-list">
                        {currentItems.map((conversion, index) => {
                            const pathId = `${conversion.conversionType}:${getConversionDbId(conversion) || ''}`;
                            const pathOpen = Boolean(expandedItems[index]) || (openPathId != null && openPathId === pathId);
                            return (
                            <li key={pathId || index} className={`conversion-item ${conversion.conversionType !== 'sale' ? 'other-conversion-item' : ''}`}>
                                <div className="conversion-header" onClick={() => toggleExpand(index, pathId)}>
                                    <div className="conversion-main">
                                        {/* Show conversion value for non-stripe sales */}     
                                        {conversion.conversionType !== 'sale' ? (
                                            <div className="conversion-value-indicator body-small-semibold">
                                                {conversion.conversionType === 'funnel_step' ? (
                                                    <span className="funnel-step-badge">
                                                        {conversion.step_name}
                                                    </span>
                                                ) : conversion.conversionType === 'email_signup' ? (
                                                    <span className="conversion-amount-email body-small-semibold">
                                                        <FontAwesomeIcon icon={faEnvelope} />
                                                    </span>
                                                ) : (
                                                    <span className="conversion-amount-call body-small-semibold">
                                                        <FontAwesomeIcon icon={faPhone} />
                                                    </span>
                                                )}
                                            </div>
                                        ) : conversion.offer_type === 'signup' ? (
                                            <div className="conversion-value-indicator">
                                                <span className="conversion-amount-email body-small-semibold">
                                                    <FontAwesomeIcon icon={faEnvelope} />
                                                </span>
                                            </div>
                                        ) : conversion.value > 0 ? (
                                            <div className="conversion-value-indicator">
                                                {editingConversionValue === (conversion.sale_id || conversion.conversion_id) ? (
                                                    <div className="conversion-value-edit">
                                                        <input
                                                            type="number"
                                                            value={newConversionValue}
                                                            onChange={(e) => setNewConversionValue(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="conversion-value-input"
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                        <div className="conversion-value-buttons">
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handleSaveConversionValue(); }}
                                                                disabled={isUpdatingValue}
                                                                className="save-conversion-value-btn"
                                                            >
                                                                {isUpdatingValue ? 'Saving...' : 'Save'}
                                                            </button>
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handleCancelEditConversionValue(); }}
                                                                disabled={isUpdatingValue}
                                                                className="cancel-conversion-value-btn"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="conversion-amount-container">
                                                        <span className="conversion-amount body-small-semibold">
                                                            +{getCurrencySymbol(data?.currency || 'USD')}{conversion.value}
                                                        </span>
                                                        <Icon 
                                                            name="edit" 
                                                            size={12} 
                                                            onClick={(e) => { e.stopPropagation(); handleEditConversionValue(conversion); }}
                                                            style={{cursor: 'pointer', marginLeft: '4px'}}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        ) : <></>}
                                        <div className="conversion-text">
                                            <div className='conversion-text-upper'>
                                                {hasResource(conversion) ? (
                                                    <>
                                                        <strong>{conversion.offer_name}</strong> conversion from {conversion.resource_type}{' '}
                                                        <span className="conversion-source-chip conversion-source-chip--tracked">
                                                            <ResourceSourceLogo resourceType={conversion.resource_type} size="sm" />
                                                            {getResourceDisplayName(conversion)}
                                                        </span>
                                                    </>
                                                ) : isDirectSource(conversion) ? (
                                                    <>
                                                        <strong>{conversion.offer_name}</strong> conversion from{' '}
                                                        <span
                                                            className="conversion-source-chip conversion-source-chip--referrer"
                                                            tabIndex={0}
                                                            onClick={(e) => e.stopPropagation()}
                                                            aria-label="This conversion likely originated from a user who directly typed the landing page url into their browser address bar"
                                                        >
                                                            <ReferrerSourceLogo host="direct" size="sm" />
                                                            Direct
                                                            <FontAwesomeIcon icon={faCircleInfo} className="conversion-referrer-info-icon" aria-hidden="true" />
                                                            <span className="conversion-source-tooltip" role="tooltip">
                                                                This conversion likely originated from a user who directly typed the landing page url into their browser address bar
                                                            </span>
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>{conversion.offer_name}</strong> conversion from{' '}
                                                        <span
                                                            className="conversion-source-chip conversion-source-chip--referrer"
                                                            tabIndex={0}
                                                            onClick={(e) => e.stopPropagation()}
                                                            aria-label="This conversion likely originated from a non-tracking link, hence the exact source cannot be shown"
                                                        >
                                                            <ReferrerSourceLogo host={getReferrerHost(conversion.initial_referrer)} size="sm" />
                                                            {getReferrerHost(conversion.initial_referrer)}
                                                            <FontAwesomeIcon icon={faCircleInfo} className="conversion-referrer-info-icon" aria-hidden="true" />
                                                            <span className="conversion-source-tooltip" role="tooltip">
                                                                This conversion likely originated from a non-tracking link, hence the exact source cannot be shown
                                                            </span>
                                                        </span>
                                                    </>
                                                )}
                                                {conversion.country_code && (
                                                    <span className="conversion-country-flag" title={countryName(conversion.country_code)} aria-label={countryName(conversion.country_code)}>
                                                        {countryFlag(conversion.country_code)}
                                                        <span className="conversion-country-tooltip" role="tooltip">
                                                            {countryName(conversion.country_code)}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                            <div className="conversion-email-row">
                                            {(() => {
                                                const leadEmails = getLeadEmails(conversion);
                                                const leadName = getLeadName(conversion);
                                                const rowId = getConversionRowId(conversion, index);
                                                const identityLabel = [leadName, leadEmails.join(', ')].filter(Boolean).join(' · ');
                                                const canAddContact = Boolean(conversion.lead_id || getConversionDbId(conversion));
                                                const missingContact = !leadName && leadEmails.length === 0;

                                                return (
                                                    <>
                                                        {identityLabel && (
                                                            conversion.lead_id ? (
                                                                <Link
                                                                    to={`/audience?lead=${encodeURIComponent(conversion.lead_id)}`}
                                                                    className="conversion-emails conversion-email-link body-small-semibold"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {identityLabel}
                                                                </Link>
                                                            ) : (
                                                                <div className="conversion-emails body-small-semibold">
                                                                    {identityLabel}
                                                                </div>
                                                            )
                                                        )}
                                                        {canAddContact && missingContact && (
                                                            addingEmailKey === rowId ? (
                                                                <form
                                                                    className="conversion-add-email-form"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onSubmit={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        saveLeadContact(conversion);
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="email"
                                                                        className="conversion-add-email-input"
                                                                        value={emailDraft}
                                                                        onChange={(e) => setEmailDraft(e.target.value)}
                                                                        placeholder="Email"
                                                                        aria-label="Email"
                                                                        autoFocus
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        className="conversion-add-email-input"
                                                                        value={nameDraft}
                                                                        onChange={(e) => setNameDraft(e.target.value)}
                                                                        placeholder="Name"
                                                                        aria-label="Name"
                                                                        maxLength={200}
                                                                    />
                                                                    <button
                                                                        type="submit"
                                                                        className="conversion-add-email-submit"
                                                                        disabled={isSavingEmail || (!emailDraft.trim() && !nameDraft.trim())}
                                                                    >
                                                                        {isSavingEmail ? 'Saving...' : 'Save'}
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className="conversion-add-email-cancel"
                                                                        onClick={(e) => { e.stopPropagation(); cancelAddEmail(); }}
                                                                        disabled={isSavingEmail}
                                                                    >
                                                                        Cancel
                                                                    </button>
                                                                    {emailError && (
                                                                        <span className="conversion-add-email-error">{emailError}</span>
                                                                    )}
                                                                </form>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="conversion-add-email"
                                                                    onClick={(e) => { e.stopPropagation(); openAddEmail(rowId); }}
                                                                >
                                                                    Add email/name
                                                                </button>
                                                            )
                                                        )}
                                                    </>
                                                );
                                            })()}
                                            {conversion.is_test && (
                                                <p className="conversion-test-tag">This is a test conversion - you may delete it from the trash icon on the right</p>
                                            )}
                                            </div>
                                            <div className="conversion-timestamp body-small-regular">
                                                {new Date(conversion.timestamp).toLocaleString()}
                                            </div>
                                        </div>
                                    
                                        <div className="conversion-actions">
                                            {conversion.related_events && conversion.related_events.length > 0 && (
                                                <button 
                                                    className="toggle-button" 
                                                    onClick={(e) => { e.stopPropagation(); toggleExpand(index, pathId); }}
                                                    aria-label={pathOpen ? "Hide related events" : "Show related events"}
                                                >
                                                    <FontAwesomeIcon icon={pathOpen ? faMinus : faPlus} />
                                                </button>
                                            )}
                                            {conversion.conversionType === 'call_booking' && (
                                                <button 
                                                    className="record-sale-button"
                                                    onClick={(e) => { e.stopPropagation(); handleRecordSale(conversion); }}
                                                    title="Record sale for this call booking"
                                                >
                                                    Record Sale
                                                </button>
                                            )}
                                            <div className="delete-offer-button" >
                                            <Icon name="delete" size={20}  color="var(--Delete-Red)" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteConversion(
                                                        conversion.conversionType === 'call_booking' ? conversion.booking_id : 
                                                        conversion.conversionType === 'funnel_step' ? conversion.conversion_id :
                                                        conversion.conversionType === 'email_signup' ? conversion.signup_id :
                                                        conversion.sale_id, 
                                                        conversion.conversionType
                                                    );
                                                }} />
                                                </div>
                                        </div>
                                    </div>
                                </div>
                            
                                {pathOpen && conversion.related_events && conversion.related_events.length > 0 && (
                                    <div className="related-clicks-container">
                                        <div className="related-clicks">
                                            <h4>Related Events:</h4>
                                            <ul>
                                                {conversion.related_events
                                                    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                                                    .map((event, eventIndex) => (
                                                        <li key={eventIndex}>
                                                            <div className="event-item">
                                                                <div className="event-icon">
                                                                    {event.event_type === 'click' && <Icon name="click" size={16} color="var(--Primary-500)" />}
                                                                    {event.event_type === 'sale' && event.offer_type === 'signup' && <FontAwesomeIcon icon={faEnvelope} />}
                                                                    {event.event_type === 'sale' && event.offer_type !== 'signup' && (
                                                                        <span className="sale-amount">+{getCurrencySymbol(data?.currency || 'USD')}{event.value}</span>
                                                                    )}
                                                                    {event.event_type === 'call_booking' && <FontAwesomeIcon icon={faPhone} />}
                                                                    {event.event_type === 'email_signup' && <FontAwesomeIcon icon={faEnvelope} />}
                                                                    {event.event_type === 'funnel_step' && <span className="funnel-step-indicator">{event.step_name}</span>}
                                                                </div>
                                                                <div className="event-details">
                                                                    {new Date(event.timestamp).toLocaleString()} - {getEventActionLabel(event)}{' '}
                                                                    {hasResource(event) ? (
                                                                        <>
                                                                            on {event.resource_type}{' '}
                                                                            <strong>{getResourceDisplayName(event)}</strong>
                                                                        </>
                                                                    ) : isDirectSource(event) ? (
                                                                        <>
                                                                            from <strong>Direct</strong>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            from {getReferrerHost(event.initial_referrer)}
                                                                        </>
                                                                    )}
                                                                    {' '}for <strong>{event.offer_name}</strong>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    ))}
                                            </ul>
                                        </div>
                                    </div>
                                )}
                            </li>
                            );
                        })}
                    </ul>
                    
                    {listedConversions.length > itemsPerPage && (
                        <div className="pagination-controls">
                            <button 
                                onClick={goToPreviousPage} 
                                disabled={currentPage === 1}
                                className="pagination-button"
                            >
                                Previous
                            </button>
                            
                            <div className="pagination-info">
                                Page {currentPage} of {totalPages} 
                                <span className="pagination-summary">
                                    (Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, listedConversions.length)} of {listedConversions.length})
                                </span>
                            </div>
                            
                            <button 
                                onClick={goToNextPage} 
                                disabled={currentPage === totalPages}
                                className="pagination-button"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            ) : (
                <div className="no-data">No conversions found</div>
            )}
            {showDeleteConfirmation && (
                <div className="confirmation-overlay">
                    <div className="confirmation-popup confirmation-popup--danger">
                        <div className="confirmation-popup__header">
                            <div className="confirmation-popup__icon">&#9888;</div>
                            <h3 className="confirmation-popup__title">Delete Conversion</h3>
                        </div>
                        <p className="confirmation-popup__body">Are you sure you want to delete this conversion? This action cannot be undone.</p>
                        <div className="confirmation-popup__actions">
                            <button
                                onClick={cancelDeleteConversion}
                                className="cancel-button"
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDeleteConversion}
                                className="proceed-button"
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting…' : 'Yes, Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manual Sale Modal */}
            {showManualSaleModal && (
                <div className="modal-overlay">
                    <div className="manual-sale-modal">
                        <button className="close-modal-button" onClick={closeManualSaleModal}>×</button>
                        <ManualSale onConversionRecorded={closeManualSaleModal} />
                    </div>
                </div>
            )}

            {/* Record Sale Modal */}
            {showRecordSaleModal && (
                <div className="modal-overlay">
                    <div className="confirmation-popup">
                        <h3>Record Sale for Call Booking</h3>
                        <p>
                            Click below if you know that this call booking was closed but the sale has not been tracked yet by Revtrack. 
                            <br /><br />
                            <strong>Note:</strong> Only do this if the sale hasn't already been tracked, otherwise it will be tracked twice.
                        </p>
                        
                        <div className="sale-form-inputs">
                            <div className="input-group">
                                <label htmlFor="saleDate">When did the sale occur? *</label>
                                <input
                                    type="date"
                                    id="saleDate"
                                    value={saleDate}
                                    onChange={(e) => setSaleDate(e.target.value)}
                                    className="sale-date-input"
                                    required
                                />
                            </div>
                            
                            <div className="input-group">
                                <label htmlFor="salePrice">Sale price (optional):</label>
                                <input
                                    type="number"
                                    id="salePrice"
                                    value={salePrice}
                                    onChange={(e) => setSalePrice(e.target.value)}
                                    placeholder="Enter sale amount"
                                    className="sale-price-input"
                                    step="0.01"
                                    min="0"
                                />
                            </div>
                        </div>
                        
                        <div className="confirmation-buttons">
                            <button onClick={cancelRecordSale} disabled={isRecordingSale}>
                                Cancel
                            </button>
                            <button 
                                onClick={confirmRecordSale} 
                                className="confirm-record-sale-button"
                                disabled={isRecordingSale || !saleDate}
                            >
                                {isRecordingSale ? 'Recording...' : 'Record Sale'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Activity;
